import { LinkPlugin } from '@alpod-ai-writer/plate-plugins/link/react';
import { cn, withProps } from '@udecode/cn';
import { nanoid, NodeApi,type Value } from '@udecode/plate';
import { Plate, useEditorRef, useStoreSelect } from '@udecode/plate/react';
import { type CreatePlateEditorOptions, PlateLeaf } from '@udecode/plate/react';
import { AIPlugin } from '@udecode/plate-ai/react';
import {
    BasicMarksPlugin,
    BoldPlugin,
    ItalicPlugin,
    StrikethroughPlugin,
    UnderlinePlugin,
} from '@udecode/plate-basic-marks/react';
import { getCommentKey, getDraftCommentKey } from '@udecode/plate-comments';
import { CommentsPlugin, useCommentId } from '@udecode/plate-comments/react';
import { ArrowUpIcon } from 'lucide-react';
import React, { useEffect, useMemo } from 'react';

import { useCreateCommentsEditor } from '../../hooks/useCreateCommentsEditor.hook';
import { useDocumentEditor } from '../../hooks/useDocumentEditor.hook';
import { AILeaf } from '../elements/aiLeaf.component';
import { LinkElement } from '../elements/linkElement.component';
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from './avatar.component';
import type { TDiscussion } from './blockDiscussion.component';
import {
    discussionStore
} from './blockDiscussion.component';
import { Button } from './button.component';
import type { TComment } from './comment.component';
import { CommentsEditor, CommentsEditorContainer } from './commentsEditor.component';

export const useCommentEditor = (
    options: Omit<CreatePlateEditorOptions, 'plugins'> = {},
    deps: any[] = []
) => {
    const commentEditor = useCreateCommentsEditor(
        {
            id: 'comment',
            override: {
                components: {
                    [AIPlugin.key]: AILeaf,
                    [BoldPlugin.key]: withProps(PlateLeaf, { as: 'strong' }),
                    [ItalicPlugin.key]: withProps(PlateLeaf, { as: 'em' }),
                    [LinkPlugin.key]: LinkElement,
                    [StrikethroughPlugin.key]: withProps(PlateLeaf, { as: 's' }),
                    [UnderlinePlugin.key]: withProps(PlateLeaf, { as: 'u' }),
                    // [SlashInputPlugin.key]: SlashInputElement,
                },
            },
            plugins: [BasicMarksPlugin],
            value: [],
            ...options,
        },
        deps
    );

    return commentEditor;
};

export function CommentCreateForm({
    autoFocus = false,
    className,
    discussionId: discussionIdProp,
    focusOnMount = false,
    isSuggesting,
}: {
  autoFocus?: boolean;
  className?: string;
  discussionId?: string;
  focusOnMount?: boolean;
  isSuggesting?: boolean;
}) {
    const discussions = useStoreSelect(
        discussionStore,
        (state) => state.discussions
    );

    const { cursorData } = useDocumentEditor();
    const editor = useEditorRef();
    const discussionId = useCommentId() ?? discussionIdProp;
    const [resetKey, setResetKey] = React.useState(0);

    const [commentValue, setCommentValue] = React.useState<Value | undefined>();
    const commentContent = useMemo(
        () =>
            commentValue
                ? NodeApi.string({ children: commentValue as any, type: 'p' })
                : '',
        [commentValue]
    );
    const commentEditor = useCommentEditor({}, [resetKey]);

    useEffect(() => {
        if (commentEditor && focusOnMount) {
            commentEditor.tf.focus();
        }
    }, [commentEditor, focusOnMount]);

    const onAddComment = React.useCallback(async () => {
        setResetKey((prev) => prev + 1);

        if (discussionId) {
            // Get existing discussion
            const discussion = discussions.find((d: any) => d.id === discussionId);

            if (!discussion || !commentValue) return;

            // Create reply comment
            const comment: TComment = {
                id: nanoid(),
                contentRich: commentValue,
                createdAt: new Date(),
                discussionId,
                isEdited: false,
                userId: cursorData.identityId,
                userName: cursorData.name,
                userColor: cursorData.color,
                userAvatarUrl: cursorData.avatarUrl
            };

            // Add reply to discussion comments
            const updatedDiscussion = {
                ...discussion,
                comments: [...discussion.comments, comment],
            };

            // Filter out old discussion and add updated one
            const updatedDiscussions = discussions
                .filter((d: any) => d.id !== discussionId)
                .concat(updatedDiscussion);

            discussionStore.set('discussions', updatedDiscussions);

            return;
        }

        const commentsNodeEntry = editor
            .getApi(CommentsPlugin)
            .comment.nodes({ at: [], isDraft: true });

        if (commentsNodeEntry.length === 0) return;

        const documentContent = commentsNodeEntry
            .map(([node]) => node.text)
            .join('');

        const _discussionId = nanoid();

        const newDiscussion: TDiscussion = {
            id: _discussionId,
            comments: [
                {
                    id: nanoid(),
                    contentRich: commentValue!,
                    createdAt: new Date(),
                    discussionId: _discussionId,
                    isEdited: false,
                    userId: cursorData.identityId,
                    userName: cursorData.name,
                    userColor: cursorData.color,
                    userAvatarUrl: cursorData.avatarUrl,
                }
            ],
            createdAt: new Date(),
            documentContent,
            isResolved: false,
            userId: cursorData.identityId,
        };

        // Update discussions store
        discussionStore.set('discussions', [...discussions, newDiscussion]);

        const id = newDiscussion.id;

        commentsNodeEntry.forEach(([, path]) => {
            editor.tf.setNodes(
                {
                    [getCommentKey(id)]: true,
                },
                { at: path, split: true }
            );
            editor.tf.unsetNodes([getDraftCommentKey()], { at: path });
        });
    }, [discussionId, editor, commentValue, cursorData.identityId, discussions]);

    const onAddSuggestion = React.useCallback(async () => {
        if (!discussionId) return;

        if (!commentValue) return;

        const suggestion: TDiscussion = {
            id: discussionId,
            comments: [
                {
                    id: nanoid(),
                    contentRich: commentValue!,
                    createdAt: new Date(),
                    discussionId,
                    isEdited: false,
                    userId: cursorData.identityId,
                    userName: cursorData.name,
                    userColor: cursorData.color,
                    userAvatarUrl: cursorData.avatarUrl,
                },
            ],
            createdAt: new Date(),
            isResolved: false,
            userId: cursorData.identityId,
        };

        // Update discussions store
        discussionStore.set('discussions', [...discussions, suggestion]);
    }, [discussionId, commentValue, discussions]);

    return (
        <div className={cn('flex w-full', className)}>
            <div className="mt-1 mr-1 shrink-0">
                <Avatar className="size-6">
                    <AvatarImage alt={cursorData.name} src={cursorData.avatarUrl} />
                    <AvatarFallback>{cursorData.name}</AvatarFallback>
                </Avatar>
            </div>

            <div className="relative flex grow gap-2">
                <Plate
                    onChange={({ value }) => {
                        setCommentValue(value);
                    }}
                    editor={commentEditor}
                >
                    <CommentsEditorContainer variant="comment">
                        <CommentsEditor
                            variant="comment"
                            className="min-h-[25px] grow pt-0.5 pr-8"
                            placeholder="Reply..."
                            autoComplete="off"
                            autoFocus={autoFocus}
                        />

                        <Button
                            size="icon"
                            variant="ghost"
                            className="absolute right-0 bottom-0 ml-auto shrink-0"
                            disabled={commentContent.trim().length === 0}
                            onClick={(e) => {
                                e.stopPropagation();

                                if (isSuggesting) {
                                    void onAddSuggestion();
                                } else {
                                    void onAddComment();
                                }
                            }}
                        >
                            <div className="flex size-6 items-center justify-center rounded-full">
                                <ArrowUpIcon />
                            </div>
                        </Button>
                    </CommentsEditorContainer>
                </Plate>
            </div>
        </div>
    );
}
